/**
 * Quote Studio — the persisted entities.
 *
 * A studio is a workspace. Cost models are first-class: the header carries
 * identity, media and its place on the studio board; the recipe lives on
 * cost model versions, which are their own entities keyed back by
 * costModelId (the Product / ProductVersion shape). Quotes are nodes on the
 * board that pin cost model versions and freeze prices when released. Links
 * are child -> parent; a link into a quote is a quote line.
 *
 * Nothing here stores a rate. Cost is always recipe × today's rates, read
 * live from the released manufacturing rates and the vendor catalog.
 */

/** the audit fields every persisted studio entity carries */
export interface QuoteStudioAudit {
    createdById: string;
    createdOn: Date;
    updatedById: string;
    updatedOn: Date;
}

export type QuoteStudioMachineKey = 'largeMachineHourlyRate' | 'smallMachineHourlyRate';

export type QuoteStudioOpKey =
    'basicTrimmingCostPerMinute'
    | 'deflashingCostPerMinute'
    | 'precisionFinishingCostPerMinute'
    | 'flamePolishCostPerMinute'
    | 'assemblyCostPerMinute'
    | 'packageCostPerMinute'
    | 'qualityTestingCostPerMinute'
    | 'foamFillingCostPerMinute'
    | 'otherCostPerMinute';

/**
 * One hardware row. Two kinds:
 *  - cataloged: hardwareItemId + vendorHardwareDataId set — description, units and
 *    unit cost read THROUGH to the live component catalog and are not editable
 *  - free-form: ids null — the row owns its own values
 * Packaging rows also carry `per` (parts per container) and divide instead of multiply.
 */
export interface QuoteStudioHardwareRow {
    id: string;
    hardwareItemId: string | null;
    vendorHardwareDataId: string | null;
    description: string;
    units: string;
    quantity: number;
    unitCost: number;
    /** packaging only — how many parts one container holds */
    per?: number;
}

export type QuoteStudioHardwareKind = 'moldedIn' | 'secondary' | 'packaging';

/** one resin in a blend: its material and the pounds of it in each shot */
export interface QuoteStudioResin {
    id: string;
    materialKey: string;
    pounds: number;
}

export interface QuoteStudioVariation {
    id: string;
    name: string;
    /** false closes shot weight, resin, machine and mold-in hardware in one move */
    molded: boolean;
    shotWeight: number;
    /**
     * Nominal wall thickness in inches. Not costed - shot weight already
     * carries the resin - but it is a spec the customer reads, so it prints
     * beside the material on the quote. Optional: rows saved before it
     * existed have no value, and a non-molded variation has no wall.
     */
    nominalWallInches?: number | null;
    materialKey: string;
    /**
     * A shot blended from more than one resin - natural with a pre-compounded
     * color, say - lists each resin with its own pounds. When present it is the
     * truth: shotWeight is kept equal to the sum and materialKey to the first
     * resin, so anything reading only those two still sees the right weight and
     * the main resin. Absent on a single-resin shot (the usual case, and every
     * row saved before blends existed).
     */
    resins?: QuoteStudioResin[];
    machineKey: QuoteStudioMachineKey;
    cycleMinutes: number;
    armPercent: number;
    /**
     * Expected scrap, as a percent of good parts, applied to the whole unit
     * cost the way the costing sheet did: cost x (1 + scrap). Missing on rows
     * saved before it existed, which read as the shop's usual 1.5%.
     */
    scrapPercent?: number;
    /**
     * Sales commission as a percent of cost, the sheet's "Sales Commission"
     * line - only some accounts carry one, so it defaults to none. Applied
     * beside scrap, not compounded with it: cost x (1 + scrap + commission).
     */
    commissionPercent?: number;
    /** inserts, mold-in graphics — the tool closes around these */
    moldedIn: QuoteStudioHardwareRow[];
    /** fitted after molding, during finishing/assembly */
    secondary: QuoteStudioHardwareRow[];
    /** pallets, gaylords, cartons — priced per container, divided down */
    packaging: QuoteStudioHardwareRow[];
    /** op key -> minutes */
    ops: Partial<Record<QuoteStudioOpKey, number>>;
    /**
     * Ops switched off but whose minutes are kept, so unticking a line and
     * ticking it again does not lose what was typed. An op counts only when
     * it is in `ops` AND not listed here.
     */
    disabledOps?: QuoteStudioOpKey[];
}

export type QuoteStudioVersionStatus = 'draft' | 'released' | 'superseded';

/**
 * One version of a cost model's recipe — its own entity, keyed back to the
 * cost model by `costModelId`, the way ProductVersion hangs off Product.
 * Versions are temporal (v2 supersedes v1) and each holds the coexisting
 * variations. A released version is frozen: nothing in it changes, and it
 * pins the child versions it was costed against. Prices are never stored
 * here; they are looked up live from the rates and the catalog, which is what
 * lets margin be recomputed years later.
 */
export interface QuoteStudioCostModelVersion extends QuoteStudioAudit {
    id: string;
    costModelId: string;
    releasedById: string | null;
    versionNumber: number;
    status: QuoteStudioVersionStatus;
    releasedOn: Date | null;
    variations: QuoteStudioVariation[];
    /** which variation is live — everything upstream costs against it */
    quotedVariationId: string;
    /** each version is its own card on the board, so it carries its own place */
    x: number;
    y: number;
}

/**
 * The cost model itself carries very little — identity, media, position. The
 * recipe lives on its versions. Anything else that points at a cost model
 * (a product version, one day) resolves to its latest released version.
 */
export interface QuoteStudioCostModel extends QuoteStudioAudit {
    id: string;
    /** the studio whose board it sits on */
    studioId: string;
    partNumber: string;
    description: string;

    /**
     * Media is cost-model level, like the part number — variations share it.
     * Images upload through the shared image uploader and live in Appwrite
     * storage; models are in-memory object URLs until the module gets
     * persistence.
     */
    imageUrl: string | null;
    /** small copy of the image, kept so removing a model can fall back to it */
    imageThumbnailUrl: string | null;
    /** uploaded solid model (.glb / .gltf with embedded resources) */
    modelUrl: string | null;
    modelFileName: string | null;
    /** what node cards show — a snapshot of the model, or the image */
    thumbnailUrl: string | null;
}

/**
 * A link between two cards. `from` is always a cost model VERSION; `to` is
 * either another version (making `from` one of its components) or a quote
 * (making `from` one of its lines). Because the link names a version rather
 * than a cost model, a released recipe records exactly which child version it
 * was costed against — no separate pinning is needed.
 */
export interface QuoteStudioLink {
    id: string;
    studioId: string;
    from: string;
    to: string;
    quantity: number;
}

export interface QuoteStudioPriceBreak {
    id: string;
    quantity: number;
    /** the margin the estimator asked for — prices a draft; kept for the record once released */
    margin: number;
    /** the unit price the customer was told — set on release and never recomputed; null while a draft */
    unitPrice: number | null;
    /** the same for each option column shown on the PDF, by variation id — frozen on release */
    optionUnitPrices: Record<string, number>;
}

/**
 * One order size the calculator turns into a price break. There are as many as
 * the estimator wants: three to start with, named minimum / target / upper, but
 * a quote that needs a fourth step in the ladder just adds one.
 */
export interface QuoteStudioOrderBound {
    id: string;
    /** free text, so a bound can be called whatever the ladder needs */
    label: string;
    /** order value in dollars — the quantity is derived from it at the margin */
    orderValue: number;
    margin: number;
}

/**
 * The legacy shape: three fixed bounds. Lines saved before the calculator took
 * any number of them come back like this and are converted on load.
 */
export interface QuoteStudioLegacyOrderBounds {
    minimum: QuoteStudioOrderBound;
    target: QuoteStudioOrderBound;
    upper: QuoteStudioOrderBound;
}

export interface QuoteStudioQuoteLine {
    id: string;
    /**
     * The cost model this line prices. EMPTY for a price-only line: a part
     * carried straight from a legacy price list, with a frozen price and no
     * recipe behind it - so no cost and no margin. Such lines name their part
     * with `partNumber` / `description` instead.
     */
    costModelId: string;
    /** pinned on release so the line never re-resolves; null = follow the working version */
    costModelVersionId: string | null;
    /**
     * The part as printed. Absent on a draft line, which reads its cost model's
     * header live. Stamped on every line when the quote is released, so the
     * printed quote never changes however the header is edited afterwards; a
     * revision clears them and follows the header again. A price-only line has
     * them from the start, since it has no cost model to read.
     */
    partNumber?: string;
    description?: string;
    imageUrl?: string | null;
    thumbnailUrl?: string | null;
    /**
     * Price-only lines only: when one line prices a FAMILY of parts (the same
     * part in several colors at one price), `partNumber` is the family's base
     * number and these are the full part numbers it covers. A product finds its
     * line through this list.
     */
    memberPartNumbers?: string[];
    /** editable quantity/margin rows — the quote IS this table; there is no single
        quoted quantity, the customer picks a row */
    breaks: QuoteStudioPriceBreak[];
    /* calculator meta — order sizes in dollars, each with the margin we want at
       that size. Nothing below changes pricing until "calculate breaks" runs;
       a bound left at 0 is skipped. */
    orderBounds: QuoteStudioOrderBound[];
    /** which bound is the order actually being chased — tints its row on the board card */
    targetBoundId: string | null;
    showAlternates: boolean;
}

/** the shop's usual tooling terms — the default on a new tool, and the presets offered beside it */
export const QUOTE_STUDIO_TOOLING_TERMS_OPTIONS = [
    '100% invoiced after first-article samples · tools are customer-owned',
    '50% down, 50% after samples',
];

export interface QuoteStudioToolingLine {
    id: string;
    description: string;
    cost: number;
    quantity: number;
    /** weeks from release to delivery; the drawer turns it into an approximate date */
    leadTimeWeeks: number;
    /** which quoted part this tool belongs to — its card prints on that part's
        page; null = general, shown only on the summary page's combined card */
    costModelId: string | null;
    /** printed under this tool's line on its tooling card; empty prints nothing */
    terms: string;
}

/**
 * A PDF dropped onto the quote - a mechanical drawing, an analysis, a spec -
 * appended to the generated document in list order. The file lives in the
 * shared resource bucket; only the reference is stored here, so the same file
 * can be carried onto a quote started from this one without copying it.
 */
export interface QuoteStudioAttachment {
    id: string;
    /** the file in the shared resource bucket */
    fileId: string;
    /** the name it was dropped under - what the drawer lists */
    name: string;
    /** the storage view URL, read when the pages are appended */
    url: string;
    sizeBytes: number;
    /** first page, rendered once on upload so the card never re-parses the PDF */
    thumbnailUrl: string | null;
    /** how many pages this will add to the generated quote */
    pageCount: number | null;
}

/**
 * One term or condition printed on the quote's closing page. A quote keeps its
 * own copy of the wording rather than pointing at the standard list, so a
 * released quote prints exactly what it went out with even after the standard
 * wording is edited. `key` names the standard term it came from; a term typed
 * for this quote alone has none.
 */
export interface QuoteStudioQuoteTerm {
    id: string;
    key: string | null;
    title: string;
    text: string;
    enabled: boolean;
}

/**
 * What kind of document this is.
 *
 *  - `budgetary` a quick number for planning. Printed as an estimate, with no
 *                signature block and no invitation to order against it.
 *  - `formal`    a firm offer the customer can raise a purchase order against.
 *
 * This is about the document, not its lifecycle - either kind is drafted and
 * then released, and either kind gets a quote number when it is.
 */
export type QuoteStudioQuoteType = 'budgetary' | 'formal';

export type QuoteStudioQuoteStatus = 'draft' | 'released';

/**
 * What happened to a quote - separate from draft / released, which is about
 * whether its prices are frozen. It can be changed at any time, released or
 * not, because it records the quote's fate rather than editing it.
 */
export type QuoteStudioQuoteDisposition = 'active' | 'superseded' | 'discarded';

/** one line of a quote's status history - who changed it, when, and why */
export interface QuoteStudioQuoteStatusEntry {
    id: string;
    disposition: QuoteStudioQuoteDisposition;
    note: string;
    /** 'status' - someone set it; 'released', 'revised' and 'replaced' are recorded automatically */
    kind: 'status' | 'released' | 'revised' | 'replaced';
    changedById: string;
    /** ISO timestamp, kept as text inside the JSON row */
    changedOn: string;
}

/**
 * A quote is a node in the studio, not a cost model. A draft follows the
 * working versions of its lines and prices them from margin. Releasing it
 * cascades — every draft cost model in its tree is released first — then pins
 * each line to a version and freezes a unit price per break. Margin on a
 * released quote is always live: frozen price against today's cost.
 */
export interface QuoteStudioQuote extends QuoteStudioAudit {
    id: string;
    studioId: string;
    number: string;
    releasedById: string | null;
    /** real customer from the shared customers module */
    customerId: string | null;
    /** denormalized so the node and frozen quotes read without a lookup */
    customerName: string;
    contactId: string | null;
    contactName: string;
    /** the PDF prints the billing address only (town + state) */
    billingAddressId: string | null;
    /** budgetary is an estimate; formal can be ordered against */
    quoteType: QuoteStudioQuoteType;
    /** draft tracks the live model · released is frozen */
    status: QuoteStudioQuoteStatus;
    /** what happened to it - missing on quotes saved before it existed, which read as active */
    disposition?: QuoteStudioQuoteDisposition;
    /** every status change, oldest first, each with the reason it was made */
    statusHistory?: QuoteStudioQuoteStatusEntry[];
    releasedOn: Date | null;
    x: number;
    y: number;
    lines: QuoteStudioQuoteLine[];
    tooling: QuoteStudioToolingLine[];
    /** PDFs appended to the generated quote, in the order they are listed */
    attachments: QuoteStudioAttachment[];
    /**
     * Terms and conditions for the closing page, after the attachments. Missing
     * on quotes saved before they existed: a draft is seeded with the standard
     * set when it is opened, a released quote prints none.
     */
    terms?: QuoteStudioQuoteTerm[];
    /**
     * Whether the tooling cards print a summed total - every card on the
     * quote, not just the summary page. Off when the tools are alternatives
     * rather than a set (two mold options for one part, or parts the customer
     * chooses between): only some of them will be bought, so a total across
     * all of them is a number nobody is being asked to pay. Each tool still
     * prints its own price. (The name is historical: it began as a summary-
     * page-only switch and the stored column keeps that name.)
     */
    showSummaryToolingTotal: boolean;
    /** internal working notes for whoever is building the quote — never printed */
    notes: string;
    /**
     * Notes for the customer, printed on the quote under the prices and above
     * the signature. Missing on quotes saved before it existed, which print none.
     */
    printedNotes?: string;
    /**
     * Where the quote came from when it was not built in the studio. 'quickbooks'
     * marks a legacy price list imported as price-only lines; `sourceAsOf` is the
     * date those prices were current (ISO date). Absent on studio-built quotes.
     */
    source?: 'quickbooks';
    sourceAsOf?: string;
}

export type QuoteStudioStatus = 'active' | 'archived';

/** the workspace itself — a drawing board, not an artifact; cost models and quotes are the artifacts */
export interface QuoteStudio extends QuoteStudioAudit {
    id: string;
    name: string;
    status: QuoteStudioStatus;
    /** optional - a studio can be scratch work before anyone knows whose it is */
    customerId: string | null;
    /** denormalized so the browse page groups without loading the customer book */
    customerName: string;
    /**
     * A picture of the thing being quoted. A board full of part numbers all
     * looks the same in a list; a photograph of the assembly does not.
     */
    imageUrl: string | null;
    /** small copy, which is what the browse list shows */
    thumbnailUrl: string | null;
}

